// Catalog.ID Client — Key Grant WebSocket Client
// ============================================================================
//
// Connects to the local cld-catalog-id backend at /api/keygrant/ws, receives
// key-grant-request envelopes, orchestrates consent + the answers below, and
// ships the response back. Active only in dashboard-module context.
//
// One instance per app load. Connection is re-attempted on drop with
// exponential backoff.
//
// Modes handled:
//   register-module : record requester's Ed25519 pubkey for future signature
//                     verification. First-writer-wins.
//   release, purpose
//   bitcash-auth-
//   token           : the ONE surviving `release`. PURPOSE-routed before the
//                     mode branches (registration-wallets §4.10): it does not
//                     hand out the `bitcash_wallet` key, it mints a bounded,
//                     revocable AUTH token on the wallet the member picks.
//                     A `release` WITHOUT that purpose is refused
//                     `mode-not-supported`, exactly like an unknown mode.
//   blob-list/-get/
//   -put/-delete    : the account blob store (cid-encrypted-storage §4.3),
//                     mode-routed like `derive`. The caller's hub-attested id
//                     is the blob NAMESPACE; no consent, no keybag.
//   bitcash-wallets-
//   release/-save-
//   prepare/-save   : the bitcash manager's wallet sync (§7.3). Routed by
//                     requester and pinned to `bitcash`: the member's wallet
//                     document lives in the vault's reserved `cid` namespace,
//                     and these are the one sanctioned way its plaintext
//                     crosses a module boundary.
//
// GONE since cid-encrypted-storage M6b (2026-09-17): the generic `release`
// that ECDH-wrapped a bag entry's raw private key for the caller, and the
// two-leg `save-prepare`/`save` that took one back. Plan D-1: a module that
// needs a secret DERIVES it (`derive`, domain-separated by the caller's
// hub-attested id) or keeps an opaque document in its own blob namespace —
// neither ever moves plaintext bag material across the boundary, and the bag
// stops being a dumping ground for module state. The only plaintext handovers
// left are the consent-gated `bitcash-wallets-*` modes, which move the
// member's OWN wallet document to the one module that manages it.
//
// All inbound key-grant-request envelopes (except register-module, which
// publishes the pubkey) are signature-verified before any UI is shown.
//
// And every request of either family names the account its module works for
// (`expectedCid`, 2026-10-08): `account-gate.ts` refuses `account-mismatch`
// ahead of every handler when this tab is signed in as somebody else.
// ============================================================================

import { planReconnect, RECONNECT_INITIAL_MS } from './connection-policy';
import type { Shell } from '@catalog-shell';
import { isDashboardContext } from '../state/dashboard-context';
import { keys as keyState } from '../state/keys';
import { identity } from '../state/identity';
import { session } from '../state/session';
import * as aes from '../crypto/aes';
import { encryptEntry, requiredAlgorithmForKeyId } from '../crypto/keybag';
import { HYBRID_PUBLIC_KEY_LEN, X25519_PK_LEN } from '../crypto/kem';
import * as accountApi from '../api/account';
import { signStepUp } from '../auth/step-up';
import type { KeyGrantRequest, KeyGrantResponse, KeyGrantResponseStatus } from './types';
import type { RegistryRequest, RegistryResponse } from './registry-types';
import type { EfsRequest, EfsResponse } from './efs-types';
import type { DesktopRequest, DesktopResponse } from './desktop-types';
import type { ChatRequest, ChatResponse } from './chat-types';
import type { MailRequest, MailResponse } from './mail-types';
import type { DeriveRequest, DeriveResponse } from './derive-types';
import { dispatchDeriveRequest } from './derive-handlers';
import { setFocusRequestSender } from './unlock-gate';
import type { BlobRequest, BlobResponse } from './blob-types';
import { dispatchBlobRequest, isBlobRequestMode } from './blob-handlers';
import * as blobApi from '../api/blobs';
import { ensureDeriveRoot } from './derive-root-store';
import { dispatchRegistryRequest } from './registry-handlers';
import { listEnrollmentRecords, saveEnrollmentRecord } from './ceremony-keystore';
import { dispatchEfsRequest } from './efs-handlers';
import { dispatchDesktopRequest } from './desktop-handlers';
import { dispatchChatRequest } from './chat-handlers';
import { showChatAddressConsent } from './chat-address-consent-modal';
import { createAndShareChatAddress } from './chat-address-store';
import { dispatchMailRequest } from './mail-handlers';
import { showMailAddressConsent } from './mail-address-consent-modal';
import { showMailEncryptExportConsent } from './mail-key-export-consent-modal';
import { createMailAddressClaim } from './mail-address-store';
import {
    showOidcConsentModal, fetchAuthorizationSummary, approveWithoutDialog,
} from '../pane-types/oidc-consent-pane';
import { loginTickets } from '../state/tab-session';
import { accountGateRefusal, type AccountGateEnvelope, type AccountGateRefusal } from './account-gate';
import * as profileApi from '../api/profile';
import * as claimsApi from '../api/claims';
import { decryptClaim } from '../crypto/claims';
import { signRegistryCanonical } from '../api/registry-internal';
import { wrapForRequester } from './crypto';
import { showModuleRegistrationConsent } from './consent-modal';
import { checkPolicy, grantAlways, grantSession } from './grant-policy';
import { tryRegister, lookup as lookupModule } from './module-registry';
import { isReservedModuleId } from '../crypto/derive';
import { promptAndDeriveKey } from './supersecret-prompt';
import { requireKey } from './on-demand-key';
import { verifyRequest } from './verify';
import { BITCASH_AUTH_TOKEN_PURPOSE } from './bitcash-types';
import type { BitcashAuthTokenRequest, BitcashAuthTokenResponse } from './bitcash-types';
import { handleBitcashAuthTokenRequest } from './bitcash-handlers';
import { showBitcashAuthTokenConsent } from './bitcash-consent-modal';
import type {
    BitcashWalletsRequest, BitcashWalletsResponse,
} from './bitcash-wallets-types';
import { dispatchBitcashWalletsRequest } from './bitcash-wallets-handlers';
import { showBitcashWalletsReleaseConsent } from './bitcash-wallets-consent-modal';
import { loadWallets, addWallet } from '../state/bitcash-wallets-store';
import {
    listAccountWallets, getAccountWalletKey, getOrgWalletKey, getOrgWalletOffer,
} from './bitcash-wallet-access';
import { createBitcashWorkerClient } from './bitcash-worker-client';
import {
    DeviceDefaultTokenCache, getDefaultTokenCache, setDefaultTokenCache,
} from './bitcash-token-cache';
import { fetchRegistrationConfig } from '../auth/payment-flow';


/**
 * Per-request timeout for outbound registry-request RPC round-trips. 30s
 * accommodates the dashboard hub hop, registry-side `SignChallengeReceiver`
 * verification + ML-DSA sign, and any sidecar loopback latency. The outer
 * publish flow's user-visible step (consent modal) does NOT consume this
 * budget — it runs to completion before we send the outbound envelope.
 */
const REGISTRY_REQUEST_TIMEOUT_MS = 30_000;

interface PendingRegistryRequest {
    resolve: (value: RegistryResponse) => void;
    reject: (reason: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
}

function base64DecodeToBytes(s: string): Uint8Array {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

/**
 * Resolve the active identity's current `efs_encrypt` public key from the
 * server (`GET /api/account/publickeys` — returns only status='active' rows
 * for the authenticated account) and split the combined hybrid blob into its
 * classical (X25519, 32 B) and post-quantum (ML-KEM-1024, 1568 B) halves, the
 * shape the EFS publisher bundle expects. Sourcing the public key from the
 * server — rather than deriving it from the local key bag — means
 * `get-active-identity` does not depend on the key bag being unlocked and is
 * unaffected by selective per-device key import. Returns null when no
 * `efs_encrypt` key is registered or the stored blob is the wrong length.
 */
async function fetchActiveEfsEncryptPublicKey(): Promise<{ classicalPublicKey: string; pqPublicKey: string } | null> {
    const publicKeys = await accountApi.getOwnPublicKeys();
    // The server returns only active rows; if a rotation transiently left more
    // than one active efs_encrypt row, the highest keyIndex is the current head.
    const efs = publicKeys
        .filter(k => k.keyId === 'efs_encrypt')
        .sort((a, b) => b.keyIndex - a.keyIndex)[0];
    if (!efs) return null;
    const combined = aes.fromBase64(efs.publicKey);
    if (combined.length !== HYBRID_PUBLIC_KEY_LEN) return null;
    // Hybrid layout: x25519_pk(32) || mlkem_pk(1568).
    return {
        classicalPublicKey: aes.toBase64(combined.slice(0, X25519_PK_LEN)),
        pqPublicKey: aes.toBase64(combined.slice(X25519_PK_LEN)),
    };
}

/**
 * All claims shared WITH the viewer, decrypted with the unlocked
 * attr_encrypt private key. Undecryptable entries (rotated keys, corrupt
 * rows) are skipped rather than failing the batch. Returns [] when the
 * keybag is locked — callers gate on lock state before invoking.
 */
async function fetchDecryptedSharedClaims(): Promise<{ ownerCid: string; type: string; value: string }[]> {
    const attrKeyB64 = keyState.getPrivateKey('attr_encrypt');
    if (!attrKeyB64) return [];
    const attrKey = aes.fromBase64(attrKeyB64);
    const shared = await claimsApi.listSharedClaims();
    const out: { ownerCid: string; type: string; value: string }[] = [];
    for (const s of shared) {
        try {
            const value = await decryptClaim(s.encryptedValue, s.nonce, s.encryptedKey, attrKey);
            out.push({ ownerCid: s.ownerCid, type: s.claimType, value });
        } catch {
            // Skip claims that can't be decrypted.
        }
    }
    return out;
}

export class KeyGrantClient {
    private ws: WebSocket | null = null;
    private shell: Shell;
    private stopped = false;
    private reconnectMs = RECONNECT_INITIAL_MS;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    /** True while another Catalog.ID page holds the helper's single slot
     *  (`connection-policy.ts`): retries are slow, and immediate on focus. */
    private yielded = false;
    private readonly onPageActive = (): void => {
        if (!this.yielded || this.stopped || this.ws) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.connect();
    };
    /** Pending outbound registry-request RPCs, keyed by requestId. The
     *  message router resolves them when a matching registry-response arrives.
     *  Rejected on socket drop so callers don't hang forever. */
    private pendingRegistry = new Map<string, PendingRegistryRequest>();

    constructor(shell: Shell) {
        this.shell = shell;
    }

    start(): void {
        if (!isDashboardContext()) return;
        this.stopped = false;
        // The universal unlock redirect's outbound half (M2c): every handler
        // family's lock gate asks the hub to raise this tab through here. One
        // page, one socket, one sender — threading it through thirteen
        // dispatch-deps interfaces would buy nothing but thirteen chances to
        // forget it. A send on a closed socket is a silent no-op, which is the
        // right answer: the relay sends its own focus-request for a request no
        // tab ever received.
        setFocusRequestSender((requestId) => this.sendControl({
            type: 'focus-request', requestId,
        }));
        // The default spending token's cache (D12): the device slots, under the
        // device DEK and behind the PIN. Installed here rather than imported at
        // every use so Settings can hand in another implementation, and so a
        // build without device storage degrades to "always refill" instead of
        // failing a grant.
        if (!getDefaultTokenCache()) setDefaultTokenCache(new DeviceDefaultTokenCache());
        // A page that yielded the slot takes it back the moment the member
        // turns to it — the dashboard focuses this tab for interactive requests.
        document.addEventListener('visibilitychange', this.onPageActive);
        window.addEventListener('focus', this.onPageActive);
        this.connect();
    }

    stop(): void {
        this.stopped = true;
        setFocusRequestSender(null);
        document.removeEventListener('visibilitychange', this.onPageActive);
        window.removeEventListener('focus', this.onPageActive);
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        const ws = this.ws;
        this.ws = null;
        if (ws) {
            try { ws.close(); } catch { /* ignore */ }
        }
    }

    private connect(): void {
        if (this.stopped) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const url = `${protocol}//${window.location.host}/api/keygrant/ws`;

        let ws: WebSocket;
        try {
            ws = new WebSocket(url);
        } catch {
            this.scheduleReconnect();
            return;
        }

        this.ws = ws;

        ws.addEventListener('open', () => {
            this.reconnectMs = RECONNECT_INITIAL_MS;
            this.yielded = false;
        });

        ws.addEventListener('message', (ev) => {
            void this.handleMessage(ev.data as string);
        });

        ws.addEventListener('close', (ev) => {
            if (this.ws === ws) this.ws = null;
            this.failAllPendingRegistry('dashboard-disconnected');
            this.scheduleReconnect(ev.code);
        });

        ws.addEventListener('error', () => {
            // 'close' will fire too — let scheduleReconnect run there.
        });
    }

    private scheduleReconnect(closeCode?: number): void {
        if (this.stopped) return;
        if (this.reconnectTimer) return;
        const plan = planReconnect(closeCode, this.reconnectMs);
        this.reconnectMs = plan.nextDelayMs;
        const wasYielded = this.yielded;
        this.yielded = plan.reason === 'slot-taken';
        if (this.yielded && !wasYielded) {
            console.info('[key-grant] another Catalog.ID page holds the module connection; '
                + 'this page retries slowly and when focused');
        }
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, plan.delayMs);
    }

    private async handleMessage(raw: string): Promise<void> {
        let parsed: unknown;
        try {
            parsed = JSON.parse(raw);
        } catch {
            return;
        }
        if (!parsed || typeof parsed !== 'object') return;

        const env = parsed as { type?: string };

        // The generic account gate (2026-10-08), ahead of EVERY handler of
        // both request families: a request for another account than this tab
        // is signed in as is refused before any dialog, unlock wait or work.
        // See account-gate.ts.
        if (env.type === 'key-grant-request' || env.type === 'registry-request') {
            const refusal = accountGateRefusal(
                parsed as AccountGateEnvelope, identity.getActive()?.cid ?? null);
            if (refusal) {
                this.send(refusal);
                return;
            }
        }

        if (env.type === 'key-grant-request') {
            const request = parsed as KeyGrantRequest;
            const response = await this.handleRequest(request);
            this.send(response);
            return;
        }
        if (env.type === 'registry-request') {
            // `registry-request` is the historical envelope-type string for every
            // inter-module RPC; the requester module determines which dispatcher
            // owns the message. The handlers are deliberately kept separate so
            // each requester's pinned identity, mode set, and gate semantics
            // stay self-contained.
            const reqWithId = parsed as { requesterId?: string; mode?: string };

            // `derive` is the one MODE-routed inter-module RPC, deliberately.
            // The keybag derive primitive is module-agnostic by construction
            // (EFS lineage-labels §5b): any registered module may call it, and
            // the caller's identity is not a routing detail but the DOMAIN
            // SEPARATOR the vault mixes into the KDF. A per-requester copy
            // would have to be duplicated for every future consumer, and each
            // copy would be a chance to get the separation wrong.
            if (reqWithId.mode === 'derive') {
                const response = await this.handleDeriveRequest(parsed as DeriveRequest);
                this.send(response);
                return;
            }

            // The four account-blob modes are MODE-routed for the same reason
            // (cid-encrypted-storage §4.3): the blob store is a generic
            // per-account facility, any registered module may use it, and the
            // caller's identity is not routing metadata but the NAMESPACE the
            // vault prefixes onto every key. A per-requester copy would have to
            // be duplicated for every future consumer, and each copy would be a
            // chance to get the prefix wrong.
            if (isBlobRequestMode(reqWithId.mode)) {
                const response = await this.handleBlobRequest(parsed as BlobRequest);
                this.send(response);
                return;
            }

            if (reqWithId.requesterId === 'efs') {
                const response = await this.handleEfsRequest(parsed as EfsRequest);
                this.send(response);
                return;
            }
            // `publisher` and `work` share the desktop dispatcher: both modules
            // run the same back-channel OIDC flow and need the same
            // `oidc-consent` gates, so a parallel copy would duplicate all six.
            // Omitting a requester here does NOT reject its envelopes — they
            // fall through to the registry dispatcher below and fail there as
            // an unknown mode, which reads as a protocol bug rather than a
            // routing one.
            if (reqWithId.requesterId === 'desktop'
                || reqWithId.requesterId === 'publisher'
                || reqWithId.requesterId === 'work') {
                const response = await this.handleDesktopRequest(parsed as DesktopRequest);
                this.send(response);
                return;
            }
            if (reqWithId.requesterId === 'chat') {
                const response = await this.handleChatRequest(parsed as ChatRequest);
                this.send(response);
                return;
            }
            // Mail gets its OWN dispatcher rather than joining chat's: the two
            // families' modes carry identical field tuples and both spend an
            // account-level signing root, so the routing boundary is part of
            // the domain separation (mail migration §5.2).
            if (reqWithId.requesterId === 'mail') {
                const response = await this.handleMailRequest(parsed as MailRequest);
                this.send(response);
                return;
            }
            // The bitcash MANAGER's own family (cid-encrypted-storage §7.3):
            // the three modes that move the member's wallet KEYS, as opposed to
            // `bitcash-auth-token`, which rides the key-grant envelope, comes
            // from OTHER modules, and hands out bounded spending tokens. Routed
            // by requester, and the dispatcher pins the requester again: these
            // modes cross the blob store's namespace boundary, and §7.3 names
            // exactly one module that may.
            if (reqWithId.requesterId === 'bitcash') {
                const response = await this.handleBitcashWalletsRequest(
                    parsed as BitcashWalletsRequest);
                this.send(response);
                return;
            }
            const request = parsed as RegistryRequest;
            const response = await this.handleRegistryRequest(request);
            this.send(response);
            return;
        }
        if (env.type === 'registry-response') {
            this.handleRegistryResponse(parsed as RegistryResponse);
            return;
        }
        // Anything else is silently dropped.
    }

    private handleRegistryResponse(response: RegistryResponse): void {
        const pending = this.pendingRegistry.get(response.requestId);
        if (!pending) return;     // stale / unknown — drop silently

        // Hub-stamped sender check: defence in depth against a registered-but-
        // misbehaving module guessing a 128-bit requestId and racing a forged
        // response. The hub stamps `_senderServiceId` on every routed
        // response, including synthesised ones (`target-not-registered` etc.).
        if (response._senderServiceId && response._senderServiceId !== 'cpr') {
            this.pendingRegistry.delete(response.requestId);
            clearTimeout(pending.timer);
            pending.reject(new Error(
                `untrusted-response: registry-response from '${response._senderServiceId}', expected 'cpr'`,
            ));
            return;
        }

        this.pendingRegistry.delete(response.requestId);
        clearTimeout(pending.timer);
        pending.resolve(response);
    }

    private failAllPendingRegistry(reason: string): void {
        for (const [, p] of this.pendingRegistry) {
            clearTimeout(p.timer);
            p.reject(new Error(reason));
        }
        this.pendingRegistry.clear();
    }

    private send(
        response: KeyGrantResponse | RegistryResponse | DesktopResponse | ChatResponse | DeriveResponse
            | BlobResponse | BitcashAuthTokenResponse | AccountGateRefusal,
    ): void {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify(response));
    }

    /**
     * Send a hub CONTROL message — not an RPC envelope: no `to`, no canonical
     * bytes, no signature. Today the only one is `focus-request {requestId}`
     * (M2c), whose authority is the hub's pending-request table: the hub
     * honours it only from the connection the named request is addressed to.
     * It travels up the same socket as a response and the relay forwards it
     * verbatim (extending its own in-flight deadline on the way past).
     */
    private sendControl(message: { type: 'focus-request'; requestId: string }): void {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify(message));
    }

    private async handleRequest(
        request: KeyGrantRequest,
    ): Promise<KeyGrantResponse | BitcashAuthTokenResponse> {
        const base: Pick<KeyGrantResponse, 'type' | 'to' | 'requestId'> = {
            type: 'key-grant-response',
            to: request.requesterId,
            requestId: request.requestId,
        };

        const cid = identity.getActive()?.cid;
        if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

        // ── Trusted-sender binding (audit C2) ─────────────────────────
        // The dashboard hub stamps `_senderServiceId` with the sender's
        // bus-attested ServiceId. We refuse any envelope whose claimed
        // requesterId disagrees — this catches a registered-but-misbehaving
        // module trying to impersonate a different module by lying in
        // its envelope's `requesterId` field. Hub-side enforcement is the
        // primary line; this is defense-in-depth.
        if (!request._senderServiceId
            || request._senderServiceId !== request.requesterId) {
            return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
        }

        // ── register-module: publish pubkey first-writer-wins ─────────
        if (request.mode === 'register-module') {
            // Sender's claimed pubkey lives in the envelope's signerPubkey.
            // We refuse to register without a working signature on the
            // envelope itself (proves the sender holds the matching priv).
            if (!request.signerPubkey) {
                return { ...base, status: 'unavailable', reason: 'missing-signer-pubkey' };
            }
            if (!verifyRequest(request, request.signerPubkey)) {
                return { ...base, status: 'unavailable', reason: 'signature-invalid' };
            }

            // Reserved namespaces (cid-encrypted-storage §4.1), BEFORE the
            // consent dialog. A module registered as `cid` would derive the
            // vault's own keys and read the vault's own account blobs — and a
            // dialog asking the user to approve "cid" is not a question anyone
            // can answer, so this is refused rather than delegated.
            if (isReservedModuleId(request.requesterId)) {
                return { ...base, status: 'unavailable', reason: 'module-id-reserved' };
            }

            // Idempotent fast-path: if a (matching) registration already exists,
            // confirm without prompting. Different-pubkey re-registration takes
            // the first-writer-wins reject path below and is surfaced to the
            // user via Connected Modules' explicit override flow.
            const existing = lookupModule(cid, request.requesterId);
            if (existing) {
                return existing.publicKeyB64 === request.signerPubkey
                    ? { ...base, status: 'registered' }
                    : { ...base, status: 'unavailable', reason: 'module-already-registered' };
            }

            // Audit C2: first registration requires explicit user consent.
            // Without this gate, malware that races the legitimate module on
            // first install permanently anchors itself as that module's identity.
            const decision = await showModuleRegistrationConsent(this.shell, request);
            if (decision !== 'register') {
                return { ...base, status: 'unavailable', reason: 'registration-denied' };
            }

            const result = tryRegister(cid, request.requesterId, request.signerPubkey, request.requesterLabel);
            if (result === 'registered') return { ...base, status: 'registered' };
            return {
                ...base,
                status: 'unavailable',
                reason: result === 'reserved' ? 'module-id-reserved' : 'module-already-registered',
            };
        }

        // ── Signature gate: every other mode requires registration ────
        const registered = lookupModule(cid, request.requesterId);
        if (!registered) {
            return { ...base, status: 'unavailable', reason: 'module-not-registered' };
        }
        if (!verifyRequest(request, registered.publicKeyB64)) {
            return { ...base, status: 'unavailable', reason: 'signature-invalid' };
        }

        // ── bitcash-auth-token ────────────────────────────────────────
        // A PURPOSE-routed family inside `release` mode (registration-wallets
        // §4.10), and since cid-encrypted-storage M6b the ONLY thing a
        // `release` can mean: the module is not asking for the `bitcash_wallet`
        // key — which this tab will never release, bag-only and, for an
        // organization's, an entitled wrap rather than a handout — but for a
        // bounded, revocable AUTH token minted on the wallet the member picks.
        // The purpose route stays AHEAD of the mode check, unchanged, so these
        // envelopes are answered byte-for-byte as they were before the generic
        // release was retired.
        if (request.purpose === BITCASH_AUTH_TOKEN_PURPOSE) {
            return await this.handleBitcashAuthTokenRequest(
                request as BitcashAuthTokenRequest, base, cid);
        }

        // Every other mode, `release` without the bitcash purpose included, is
        // refused here (cid-encrypted-storage M6b): the generic raw-key release
        // and the `save-prepare`/`save` pair are gone, so a key-grant envelope
        // has nothing left to ask this vault for. A `release` that reaches this
        // line is indistinguishable from an unknown mode and is answered as one
        // rather than with a softer reason — the sender is speaking a protocol
        // version this vault retired.
        return { ...base, status: 'denied', reason: 'mode-not-supported' };
    }

    private async handleRegistryRequest(request: RegistryRequest): Promise<RegistryResponse> {
        return await dispatchRegistryRequest(request, {
            shell: this.shell,
            getActiveCid: () => identity.getActive()?.cid ?? null,
            sendSignChallenge: (params) => this.sendSignChallenge(params),
            // A8b ceremony side-effects. Injected rather than imported by the
            // handlers so the gate logic there stays testable without network
            // or modals. The authority KEY is not among them any more: since
            // cid-encrypted-storage M5 it is derived from the account's derive
            // root, and the handlers read and write its generation state
            // (`cid/registry-authority`) through their own defaults.
            listEnrollmentRecords: () => listEnrollmentRecords(),
            saveEnrollmentRecord: (record) => saveEnrollmentRecord(record),
        });
    }

    /**
     * efs → cid `registry-request` envelopes, `decapsulate-efs-encrypt` among
     * them. The one injected side-effect with a UI is `requireKey`
     * (device-setup-restructure §9.1): a member may keep `efs_encrypt` off
     * this device, and an INTERACTIVE request for a file it protects is the
     * one moment where asking for the supersecret is the right answer rather
     * than a refusal. The handler decides when to call it (unlocked bag, empty
     * chain, `requiresUserInteraction`); this closure only supplies the shell
     * the prompt opens in — which is why the dep lives here and not in the
     * handler's own imports.
     */
    private async handleEfsRequest(request: EfsRequest): Promise<EfsResponse> {
        return await dispatchEfsRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            getActiveSessionId: () => session.getSessionId(),
            getActiveEfsEncryptPublicKey: () => fetchActiveEfsEncryptPublicKey(),
            requireKey: (keyId, purpose, o) =>
                requireKey(keyId, {
                    shell: this.shell, purpose, import: o?.import ?? 'ask',
                }),
        });
    }

    /**
     * `derive` — the module-agnostic keybag derivation op (EFS lineage-labels
     * §5b). The only injected side-effect is the root's mint-and-persist,
     * which prompts for the supersecret and writes a key-bag entry; the
     * derivation itself reads the unlocked in-memory bag and mutates nothing.
     */
    private async handleDeriveRequest(request: DeriveRequest): Promise<DeriveResponse> {
        return await dispatchDeriveRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            ensureDeriveRoot: () => ensureDeriveRoot(this.shell),
        });
    }

    /**
     * The four account-blob modes (cid-encrypted-storage §4.3). No consent
     * dialog and no keybag: the vault relays ciphertext it cannot read, under
     * the member's session, inside the caller's own namespace (§4.4).
     */
    private async handleBlobRequest(request: BlobRequest): Promise<BlobResponse> {
        return await dispatchBlobRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            hasSession: () => session.isActive(),
            api: {
                listBlobs: blobApi.listBlobs,
                getBlob: blobApi.getBlob,
                putBlob: blobApi.putBlob,
                deleteBlob: blobApi.deleteBlob,
            },
        });
    }

    private async handleDesktopRequest(request: DesktopRequest): Promise<DesktopResponse> {
        return await dispatchDesktopRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            // Modal over the whole CID module — a hub-relayed consent must not
            // compete with the pane layout for attention.
            openConsentUi: (oidcRequestId) => showOidcConsentModal(this.shell, oidcRequestId),
            // Silent consent after this tab's own login (decision 1): the
            // ticket this page minted at login, the node's view of the
            // authorization, and the dialog's own approve call.
            consumeLoginTicket: (cid, ticket) => loginTickets.consume(cid, ticket),
            getAuthorization: (oidcRequestId) => fetchAuthorizationSummary(oidcRequestId),
            approveWithoutDialog: (oidcRequestId, scopes) => approveWithoutDialog(oidcRequestId, scopes),
        });
    }

    /**
     * chat → cid `registry-request` envelopes. Modes: `get-claims` (background
     * display refresh, lock-degrading), `issue-cert` (chat_sign sub-key
     * certification — ceremony-grade, waits for a keybag unlock, see
     * `chat-cert-handlers.ts`), `decap-chat-epoch` (the epoch oracle hop —
     * non-interactive, but since M2c it raises the tab and waits on a locked
     * bag like every other mode) and `create-chat-address` (the
     * contact-consent ceremony — see `chat-address-handlers.ts`).
     *
     * `create-chat-address` shows a modal AND writes a claim, so both are
     * injected to keep the gate logic unit-testable (the A8b ceremony split).
     *
     * `requireKey` (device-setup-restructure §9.1) is the other injected UI,
     * and it serves the two modes that reach for a key: `issue-cert`
     * (`chat_sign`) and `decap-chat-epoch` (`chat_encrypt`), either of which a
     * member may keep off this device. The handlers call it only when the
     * request refocuses this tab — chat's own background epoch hop never does,
     * and nobody would see a supersecret prompt raised behind a hidden tab.
     */
    private async handleChatRequest(request: ChatRequest): Promise<ChatResponse> {
        return await dispatchChatRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            getPublicProfile: (cid) => profileApi.getPublicProfile(cid),
            getDecryptedSharedClaims: () => fetchDecryptedSharedClaims(),
            confirmChatAddressShare: (input) => showChatAddressConsent(this.shell, input),
            createAndShareChatAddress: (input) => createAndShareChatAddress(input),
            requireKey: (keyId, purpose, o) =>
                requireKey(keyId, {
                    shell: this.shell, purpose, import: o?.import ?? 'ask',
                }),
        });
    }

    /**
     * mail → cid `registry-request` envelopes. Modes: `issue-mail-cert`
     * (mail_sign sub-key certification — ceremony-grade, waits for a keybag
     * unlock, no modal because unlock IS the ceremony; see
     * `mail-cert-handlers.ts`) and `create-mail-address` (the mail-address
     * record ceremony — modal-backed, see `mail-address-handlers.ts`).
     *
     * A SEPARATE dispatcher from `handleChatRequest`, deliberately: the two
     * families' modes carry identical field tuples and both reach into the
     * keybag for an account-level signing root, so the family boundary is part
     * of what keeps a chat-signed envelope from making the MAIL root sign
     * (mail migration §5.2).
     *
     * `create-mail-address` needs injected side-effects — it shows a modal
     * AND writes a claim, so both are injected to keep the gate logic
     * unit-testable (the A8b ceremony split). It deliberately does NOT publish
     * the claim or register it with the mail directory; both are separate,
     * deliberate acts on their own surfaces. `export-mail-encrypt` (the
     * operational-custody export of the account's `mail_encrypt` key, mail
     * migration §5.1) injects its consent dialog for the same reason.
     */
    /**
     * `bitcash-auth-token` (registration-wallets §4.10). Every side-effect is
     * injected so the gate logic in `bitcash-handlers.ts` stays unit-testable:
     * the consent dialog, the wallet-document read behind the account wallet,
     * the organization's key wraps, the Worker legs and the device cache.
     *
     * The ledger is the one the node names (`ledgerUrl`, `Bitcash:LedgerUrl`;
     * apps/bitcash/docs/plans/bitcash-v2.md M12) — there is no built-in one. The default
     * token is unbound and scoped to the node's service types
     * (apps/docs/plans/second-node-and-bitcash-open-issues.md §9.3); a module's own
     * token is bound to the receive address it names (D52).
     */
    private async handleBitcashAuthTokenRequest(
        request: BitcashAuthTokenRequest,
        base: Pick<BitcashAuthTokenResponse, 'type' | 'to' | 'requestId'>,
        cid: string,
    ): Promise<BitcashAuthTokenResponse> {
        return await handleBitcashAuthTokenRequest(request, base, cid, {
            listAccountWallets: () => listAccountWallets(),
            getOrgWalletOffer: () => getOrgWalletOffer(),
            confirmBitcashAuthToken: (input) => showBitcashAuthTokenConsent(this.shell, input),
            getAccountWalletKey: (choice) => getAccountWalletKey(choice),
            getOrgWalletKey: (orgCid) => getOrgWalletKey(orgCid),
            worker: createBitcashWorkerClient(),
            cache: getDefaultTokenCache(),
            // D27's caps and the service types (§9.3 D-a), from the node's runtime config
            // (`GET /api/registration/config` → `defaultToken`): go-live configuration, null
            // when the node names none.
            getDefaultTokenCaps: async () => (await fetchRegistrationConfig()).defaultToken,
            wrapForRequester: (pubkey, plaintext) => wrapForRequester(pubkey, plaintext),
            checkPolicy: (c, requesterId, keyId) => checkPolicy(c, requesterId, keyId),
            grantAlways: (c, entry) => grantAlways(c, entry),
            grantSession: (c, entry) => grantSession(c, entry),
        });
    }

    /**
     * bitcash → cid `registry-request` envelopes (cid-encrypted-storage §7.3).
     * Modes: `bitcash-wallets-release` (the member's whole wallet document,
     * wrapped to the module's ephemeral key — consent-gated, and the dialog is
     * asked BEFORE the unlock wait), `bitcash-wallets-save-prepare` (leg 1: a
     * CID-side ephemeral hybrid key) and `bitcash-wallets-save` (leg 2: one
     * wallet the manager minted or imported, appended to the document).
     *
     * Every side-effect is injected so the gate logic stays unit-testable: the
     * dialog, the policy store, the two wallet-store calls, the node's operator
     * prefix and the keybag's lock state.
     */
    private async handleBitcashWalletsRequest(
        request: BitcashWalletsRequest,
    ): Promise<BitcashWalletsResponse> {
        return await dispatchBitcashWalletsRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            isUnlocked: () => keyState.isLoaded(),
            waitForUnlock: () => keyState.waitForLoad(),
            confirmWalletsRelease: (input) =>
                showBitcashWalletsReleaseConsent(this.shell, input),
            checkPolicy: (c, requesterId, keyId) => checkPolicy(c, requesterId, keyId),
            grantAlways: (c, entry) => grantAlways(c, entry),
            grantSession: (c, entry) => grantSession(c, entry),
            loadWallets: () => loadWallets(),
            addWallet: async (record) => {
                const { slot, generation } = await addWallet(record);
                return { slot, generation };
            },
        });
    }

    private async handleMailRequest(request: MailRequest): Promise<MailResponse> {
        return await dispatchMailRequest(request, {
            getActiveCid: () => identity.getActive()?.cid ?? null,
            confirmMailAddressCreate: (input) => showMailAddressConsent(this.shell, input),
            createMailAddressClaim: (input) => createMailAddressClaim(input),
            confirmMailEncryptExport: (input) => showMailEncryptExportConsent(this.shell, input),
            // §9.1, shared by `issue-mail-cert` (`mail_sign`) and
            // `export-mail-encrypt` (`mail_encrypt`). The per-call `import` is
            // why one closure serves both: the export passes `'never'`,
            // because a module asking for the BYTES of a key the member keeps
            // off this device is no reason to start storing it here.
            requireKey: (keyId, purpose, o) =>
                requireKey(keyId, {
                    shell: this.shell, purpose, import: o?.import ?? 'ask',
                }),
        });
    }

    /**
     * Send an outbound `registry-request {mode: "sign-challenge"}` envelope to
     * the registry module via the dashboard hub and await the matching
     * `registry-response`. Used by the register-registry-key publish orchestration
     * (Phase 5 Step C4) to obtain an ML-DSA-65 signature over the validator's
     * challenge bytes — proves registry controls the private half of the
     * key being published, before the claim is shared with operators.
     *
     * The cld-id server signs the canonical bytes with its `ModuleIdentity`
     * Ed25519 key (loopback `POST /api/internal/sign-registry-canonical`); the
     * private key never crosses the browser boundary.
     */
    private async sendSignChallenge(params: {
        claimUuid: string;
        fingerprint: string;
        challengeBase64: string;
    }): Promise<RegistryResponse> {
        const ws = this.ws;
        if (ws === null || ws.readyState !== WebSocket.OPEN) {
            throw new Error('dashboard-not-connected');
        }

        const requestId = crypto.randomUUID().replace(/-/g, '');
        const requestedAt = new Date().toISOString();

        // Ask the cld-id server to build canonical bytes and sign them with
        // its ModuleIdentity. Browser never touches the Ed25519 private key.
        const signed = await signRegistryCanonical({
            mode: 'sign-challenge',
            to: 'cpr',
            requestId,
            requesterId: 'cid',
            requestedAt,
            claimUuid: params.claimUuid,
            fingerprint: params.fingerprint,
            challengeBase64: params.challengeBase64,
        });

        const envelope = {
            type: 'registry-request',
            to: 'cpr',
            requestId,
            requesterId: 'cid',
            mode: 'sign-challenge',
            requestedAt,
            claimUuid: params.claimUuid,
            fingerprint: params.fingerprint,
            challengeBase64: params.challengeBase64,
            requiresUserInteraction: false,
            signature: signed.signature,
            signerPubkey: signed.signerPubkey,
        };

        return await new Promise<RegistryResponse>((resolve, reject) => {
            const timer = setTimeout(() => {
                if (this.pendingRegistry.delete(requestId)) {
                    reject(new Error(`timeout: no registry-response within ${REGISTRY_REQUEST_TIMEOUT_MS}ms`));
                }
            }, REGISTRY_REQUEST_TIMEOUT_MS);

            this.pendingRegistry.set(requestId, { resolve, reject, timer });

            try {
                ws.send(JSON.stringify(envelope));
            } catch (err) {
                this.pendingRegistry.delete(requestId);
                clearTimeout(timer);
                reject(err instanceof Error ? err : new Error('ws-send-failed'));
            }
        });
    }
}
